/**
 * Strip secrets out of URLs before they reach analytics or ad tags.
 *
 * The photobook bio link (`/{locale}/photobook/bio/{token}`) carries a
 * signed token that both opens the customer's form and spells out their
 * first name and pet names, so it must never be copied into PostHog, GTM,
 * the Facebook pixel or anyone else's logs. Any URL or path string that
 * contains it (plain, or percent-encoded inside another URL's query, as in
 * a referrer or a redirect parameter) gets the token swapped for a marker.
 */

export const REDACTED = '[redacted]';

// The token is base64url plus a dot; stop at anything that ends a path
// segment, so the rest of the URL (query, hash) is kept as it was.
const PLAIN = /(\/photobook\/bio\/)[^/?#&\s"'<>]+/gi;
// The same path inside another URL's query string: /photobook%2Fbio%2F...
const ENCODED = /(photobook%2Fbio%2F)[A-Za-z0-9_.~-]+/gi;

export default function redactSensitiveUrl(value) {
  if (typeof value !== 'string' || !value) return value;
  if (!/photobook/i.test(value)) return value;

  return value.replace(PLAIN, `$1${REDACTED}`).replace(ENCODED, `$1${encodeURIComponent(REDACTED)}`);
}

/** True when this path is a bio link whose token is still in it. */
export function hasSensitiveUrl(value) {
  return typeof value === 'string' && redactSensitiveUrl(value) !== value;
}

/**
 * The same for every string inside an object (event properties, person
 * properties). Arrays and nested objects are walked to a fixed depth, so a
 * pathological payload cannot hang the page.
 */
export function redactDeep(value, depth = 6) {
  if (typeof value === 'string') return redactSensitiveUrl(value);
  if (depth <= 0 || !value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => redactDeep(item, depth - 1));
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;

  const out = {};
  Object.keys(value).forEach((key) => { out[key] = redactDeep(value[key], depth - 1); });

  return out;
}
